import type { SceneObject } from '../../core/SceneObject';
import type { TraceScene } from '../../core/TraceScene';
import type { PropertiesData, TransformOverride, TransformComponent, TransitionConfig, ResolvedPose } from '../../scenedata/behaviors/propertiesdata';
import { hasBoundComponent, numericTuple } from '../../scenedata/behaviors/propertiesdata';
import type { PropertyValue } from '../../behaviors/tracebehavior';
import { Properties } from '../../behaviors/properties';
import { getUndoManager } from '../undo/UndoManager';
import { BehaviorCommitCommand } from '../undo/BehaviorCommitCommand';
import { AddBehaviorCommand } from '../undo/AddBehaviorCommand';
import { CompositeCommand } from '../undo/CompositeCommand';
import type { UndoCommand } from '../undo/UndoCommand';
import { dispatchBehaviorChanged } from '../undo/editor-events';
import { Logger } from '../../utils/Logger';
import * as statesModel from './statesModel';
import { buildStateRefHealCommands } from './stateRefHeal';
import { Matrix, Quaternion, Vector3 } from '@babylonjs/core/Maths/math.vector';
import { wrapInRigContainer, unwrapRigContainer, hasRigContainer, isRigContainer } from './rigContainer';
import { isGroupBoundary, groupDescendants } from './groupBoundaries';
import { Renderer } from '../../behaviors/renderer';
import { Text } from '../../behaviors/text';
import { boundPropertyName } from '../../behaviors/tracebehavior';
import { findPropertyProvider } from '../property-provider';
import { buildOrphanedBindingTeardownCommands } from '../animation-binding-teardown';

// ---------------------------------------------------------------------------
// State-group ACTIONS — the coordination layer over per-object Properties.states.
//
// Staged into trace-viewer/editor from TraceFrontend (viewer/editor/statesActions.ts)
// as Phase 2 of the state-group centralization. Scope: the COORDINATION
// functions (owner resolution, lazy seeding, fan-out, switch, transform capture,
// transitions, ungroup). The Renderer-binding helpers (setStateColor/Material/
// Opacity) are intentionally DEFERRED — they are orthogonal and live in the host
// editor for now.
//
// Differences from the TraceFrontend original:
//   - Undo is the SHARED trace-viewer stack (getUndoManager + BehaviorCommitCommand),
//     called directly — no injected undo interface needed (already shared).
//   - The play-mode guard is the only host-specific concern → a module-level
//     `configureStateActions({ mutationGuard })`. Without a guard, actions are
//     refused in Play (see `withGuard`); TraceFrontend's guard leaves Play first.
//   - "Base is not a state" model (statesModel): no seeded Base entry; switch to
//     Base is `setState(undefined)`.
//   - B1′ ungroup: KEEP states carrying overrides (portable), drop only empty seeds.
// ---------------------------------------------------------------------------

// --- Host configuration -----------------------------------------------------

interface StateActionsConfig {
  /**
   * Runs before a persistent mutation lands, e.g. to leave Play. Without a
   * guard, actions are refused while the target's scene is in Play; with one,
   * they run after it.
   */
  mutationGuard?: () => Promise<void> | void;
}

let _config: StateActionsConfig = {};

/**
 * Configure host-specific hooks once at editor init.
 *
 * `mutationGuard` runs at most ONCE per top-level action, regardless of how many
 * Properties commits the action fans out (owner + N members) or how deeply the
 * action nests internal helpers. Re-entrant calls share the outermost guard pass
 * (see `withGuard`). The guard may still be async and meaningful — it just won't
 * fire N+1 times for one user action.
 *
 * With no `mutationGuard`, an action whose target's scene is in Play is refused:
 * it changes nothing, pushes no undo entry, and returns its no-op result (`null`,
 * `false`, `{ command: null }`, ...). A host that wants edits in Play to land
 * configures a guard that leaves Play first.
 */
export function configureStateActions(config: StateActionsConfig): void {
  _config = config;
}

/** True when a states edit on `obj` would be refused: its scene is in Play and no host guard is configured. */
export function stateEditRefused(obj: SceneObject): boolean {
  return !_config.mutationGuard && obj.traceScene.isPlayMode();
}

// --- Small helpers ----------------------------------------------------------

/**
 * Run `fn` with the mutation guard satisfied exactly once, and serialize
 * top-level actions so they never interleave.
 *
 * Three concerns are handled together:
 *  - REFUSAL: with no host guard, a top-level action on an object whose scene is
 *    in Play returns `refused` without running `fn`.
 *  - GUARD-ONCE: every exported mutating action wraps its body in `withGuard`;
 *    nested actions/helpers re-enter (depth > 0) and reuse the already-satisfied
 *    guard rather than re-firing it.
 *  - SERIALIZATION: a top-level action (depth 0) suspends at its inner `await`s
 *    while `_guardDepth === 1`. Without serialization a second top-level action
 *    dispatched during that window would see depth 1, treat itself as nested,
 *    SKIP the guard, and interleave its commit snapshots with the first. So
 *    top-level entries queue on `_actionChain`: the second awaits the first's
 *    full completion before running its guard + body. Nested entries bypass the
 *    queue (they're already inside the holder's turn).
 */
let _guardDepth = 0;
let _actionChain: Promise<unknown> = Promise.resolve();

async function withGuard<T>(obj: SceneObject, refused: T, fn: () => Promise<T>): Promise<T> {
  // Nested call — already inside a top-level action's turn. Run inline; do NOT
  // queue (queuing on the chain we're part of would deadlock) and do NOT re-fire
  // the guard.
  if (_guardDepth > 0) {
    _guardDepth++;
    try {
      return await fn();
    } finally {
      _guardDepth--;
    }
  }

  // Top-level call — chain after any in-flight action so they run strictly in
  // order. The predecessor's rejection is swallowed onto the chain (its own
  // caller already saw it) so one failed action doesn't reject queued successors.
  const run = _actionChain.then(async () => {
    if (stateEditRefused(obj)) {
      Logger.log(`[statesActions] Refused a states edit on ${obj.name} in Play`);
      return refused;
    }
    if (_config.mutationGuard) await _config.mutationGuard();
    _guardDepth++;
    try {
      return await fn();
    } finally {
      _guardDepth--;
    }
  });
  _actionChain = run.catch(() => undefined);
  return run;
}

function ownProperties(obj: SceneObject): Properties | null {
  return obj.getBehavior(Properties);
}

/**
 * The object's LIVE local transform as an absolute-pose TransformOverride. With
 * absolute state transforms (ABSOLUTE_STATE_TRANSFORMS.md), capturing the current
 * pose is just "read the live local pose". The single source of truth for this
 * math — exported via `trace-viewer/editor` and consumed by host editors (the
 * sandbox `state-capture.ts` / `keyframe-capture.ts` re-export it) so there's no
 * drift between the state-capture and keyframe-capture paths.
 */
export function liveLocalPose(obj: SceneObject): TransformOverride {
  const t = obj.transform;
  // A node in Euler mode holds its rotation in `rotation`, with no quaternion.
  const q = t.rotationQuaternion ?? Quaternion.FromEulerVector(t.rotation);
  return {
    position: [t.position.x, t.position.y, t.position.z],
    rotation: [q.x, q.y, q.z, q.w],
    scale: [t.scaling.x, t.scaling.y, t.scaling.z],
  };
}

/** A position or scale a layout writes is not stored in a state or page: the solve would replace it. */
export function withoutOwnedFields(object: SceneObject, pose: TransformOverride): TransformOverride {
  const owned = object.getRuntimeOwnedTransformFields();
  if (!owned.position && !owned.scale) return pose;
  const kept = { ...pose };
  if (owned.position) delete kept.position;
  if (owned.scale) delete kept.scale;
  return kept;
}

/**
 * Notify editors that an object's Properties changed. CRITICAL for these state
 * patches: they commit `{ states }` WITHOUT `properties`, so the Properties
 * behavior's `onValueChanged` (which only fires for `properties` patches) does
 * NOT fire. Without this event, a behaviors panel that cached the pre-edit data
 * (empty states) would clobber the just-added states on its next property edit
 * (commit is a shallow merge). The panel listens to BEHAVIOR_CHANGED to re-seed.
 */
function notifyPropsChanged(obj: SceneObject): void {
  dispatchBehaviorChanged({ objectPath: obj.getPath(), behaviorType: 'Properties', action: 'updated' });
}

/**
 * Commit a Properties patch and push one BehaviorCommitCommand. Snapshots
 * before/after so undo restores wholesale (mirrors the keyframe-capture path).
 * The mutation guard is satisfied by the enclosing `withGuard` at the action
 * entry point — NOT here, so a fan-out doesn't re-fire it per object.
 */
async function commitProps(obj: SceneObject, patch: Partial<PropertiesData>): Promise<void> {
  return withGuard(obj, undefined, async () => {
    const props = ownProperties(obj);
    if (!props) return;
    const before = props.getData();
    const errors = await props.commit(patch);
    if (errors.length > 0) {
      // Patch was rejected (schema validation) and rolled back. Don't push a
      // phantom no-op undo entry; surface the failure instead.
      Logger.warn(`[statesActions] Properties commit on ${obj.name} rejected`, errors);
      return;
    }
    const after = ownProperties(obj)?.getData() ?? before;
    getUndoManager().push(
      new BehaviorCommitCommand(obj, 'Properties', before, after, null),
    );
    notifyPropsChanged(obj);
  });
}

/**
 * Build (but do NOT push) a Properties commit command — for atomic bundling.
 * Guard is satisfied by the enclosing `withGuard` (not re-fired per fan-out item).
 */
async function buildPropsCommand(obj: SceneObject, patch: Partial<PropertiesData>): Promise<UndoCommand | null> {
  return withGuard(obj, null, async () => {
    const props = ownProperties(obj);
    if (!props) return null;
    const before = props.getData();
    const errors = await props.commit(patch);
    if (errors.length > 0) {
      // Rejected + rolled back → no command to bundle (a no-op command would
      // make undo restore identical data). Surface the failure.
      Logger.warn(`[statesActions] Properties commit on ${obj.name} rejected`, errors);
      return null;
    }
    const after = ownProperties(obj)?.getData() ?? before;
    notifyPropsChanged(obj);
    return new BehaviorCommitCommand(obj, 'Properties', before, after, null);
  });
}

/** The property names bound whole (`$name` / `{name}`) anywhere in `value`. */
function boundNamesIn(value: unknown, names = new Set<string>()): Set<string> {
  const name = boundPropertyName(value);
  if (name !== null) names.add(name);
  else if (Array.isArray(value)) value.forEach(item => boundNamesIn(item, names));
  else if (value !== null && typeof value === 'object') Object.values(value).forEach(item => boundNamesIn(item, names));
  return names;
}

/**
 * Add-state pin (pure data transform on `data`): every property a field of
 * `obj`'s Renderer is ALREADY bound to (a slot's material, colour, finish and
 * opacity, the whole-object opacity, cast shadows, a texture scale) gets the
 * value it currently resolves to echoed into `states[stateName].values`.
 *
 * A state that carries no value for a bound field follows Base, so without
 * the pin a later Base edit would leak into the new state. UNBOUND fields are
 * left untouched: binding happens when the user first sets one on a state.
 * Skips props already present in the state (never clobbers a real override) and
 * degrades to a no-op with no Renderer. Returns `data` unchanged when nothing pins.
 */
function pinBoundRendererIntoState(
  obj: SceneObject,
  props: Properties,
  data: PropertiesData,
  stateName: string,
): PropertiesData {
  const renderer = obj.getBehavior(Renderer);
  if (!renderer) return data;

  const existingValues = data.states?.[stateName]?.values ?? {};
  let next = data;
  for (const name of boundNamesIn(renderer.getData())) {
    if (Object.hasOwn(existingValues, name)) continue; // never clobber a real override
    // Variant-set declarations are deliberately NOT pinned: variants must follow
    // Base so states and variant picks compose (VARIANTS_DESIGN.md §8.2). The
    // provider may be an ancestor, so resolve it rather than reading `data`
    // (this object's own PropertiesData) alone.
    const provider = findPropertyProvider(obj, name);
    const decl = (provider?.properties.getData().properties ?? []).find(d => d.name === name);
    // `.length > 0`: an emptied `variants: []` is not a live variant set — keep
    // in sync with getMaterialVariantSet / renderer-bindings' hasVariants.
    if (decl?.type === 'color' && decl.variants && decl.variants.length > 0) continue;
    // The CURRENTLY-RESOLVING value, NOT strictly Base: if add-state runs
    // while another state is active, we pin that active value — intentional,
    // mirroring liveLocalPose/d.active which also key the live pose/visibility.
    const resolved = props.getValue(name);
    if (resolved === undefined) continue;
    next = statesModel.setStateValue(next, stateName, name, resolved);
  }
  return next;
}

/**
 * Add-state pin for a Text whose `opacity` is ALREADY bound, same rule as
 * {@link pinBoundRendererIntoState}: echo the resolving value into the new
 * state so a later Base edit doesn't leak in. Unbound is left alone.
 */
function pinBoundTextOpacityIntoState(
  obj: SceneObject,
  props: Properties,
  data: PropertiesData,
  stateName: string,
): PropertiesData {
  const name = boundPropertyName(obj.getBehavior(Text)?.getData().opacity);
  if (name === null || Object.hasOwn(data.states?.[stateName]?.values ?? {}, name)) return data;
  const resolved = props.getValue(name);
  return resolved === undefined ? data : statesModel.setStateValue(data, stateName, name, resolved);
}

/** Everything add-state pins into the new state: bound Renderer fields, then a bound Text opacity. */
function pinBoundIntoState(
  obj: SceneObject,
  props: Properties,
  data: PropertiesData,
  stateName: string,
): PropertiesData {
  return pinBoundTextOpacityIntoState(obj, props, pinBoundRendererIntoState(obj, props, data, stateName), stateName);
}

/**
 * Push a fan-out's per-object commits as ONE undo entry. A group mutation
 * (add/clone/rename/delete a state) touches the owner plus every seeded member;
 * pushing them separately would let a single Ctrl+Z undo only the last member,
 * leaving the group structurally inconsistent (owner + N−1 members changed, one
 * not). Bundling makes the whole fan-out atomic. No-op on an empty list.
 */
function bundleCommands(label: string, commands: Array<UndoCommand | null>): UndoCommand | null {
  const real = commands.filter((c): c is UndoCommand => c !== null);
  if (real.length === 0) return null;
  return real.length === 1 ? real[0] : new CompositeCommand(label, real);
}

function pushBundle(label: string, commands: Array<UndoCommand | null>): void {
  const bundled = bundleCommands(label, commands);
  if (bundled) getUndoManager().push(bundled);
}

/**
 * Ensure `obj` has a Properties behavior, WITHOUT pushing undo. Returns the
 * add command (for the caller to bundle) when Properties was created, else
 * null. Internal building block — external callers use `ensureStatesObject`.
 */
async function ensureStatesObjectCommand(
  obj: SceneObject,
): Promise<{ props: Properties; addCmd: UndoCommand | null } | null> {
  return withGuard(obj, null, async () => {
    const existing = ownProperties(obj);
    if (existing) return { props: existing, addCmd: null };
    const props = await obj.addBehavior(Properties, { properties: [], states: {} } as never);
    // Prime the saved-transform baseline from authored sceneData IMMEDIATELY, before
    // anything can re-capture. addBehavior is itself a commit, and an editor host can
    // synchronously refreshSceneData()/captureTransform() off the resulting
    // behavior-changed event. If the live node has been gizmo-moved (a member's first
    // state-routed transform edit), an unprimed Properties makes getAuthoredTransform()
    // return undefined → captureTransform falls back to the moved live node → the move
    // leaks into authored sceneData.transform (Base). Priming here closes that window
    // for every ensureStatesObject caller, not just setStateTransform.
    props.primeSavedTransform();
    // Record the add so undo removes the freshly-created Properties — otherwise a
    // first-ever state edit on a bare object left an orphan empty Properties behind
    // on undo (the commit was undoable, its host behavior was not).
    return { props, addCmd: new AddBehaviorCommand(obj, 'Properties', { properties: [], states: {} } as never) };
  });
}

/** Ensure `obj` has a Properties behavior; create an empty one (with undo) if absent. */
export async function ensureStatesObject(obj: SceneObject): Promise<Properties | null> {
  const result = await ensureStatesObjectCommand(obj);
  if (!result) return null;
  if (result.addCmd) getUndoManager().push(result.addCmd);
  return result.props;
}

// --- 1. Owner resolution ----------------------------------------------------

/**
 * The nearest self-or-ancestor Properties behavior that owns a non-empty state
 * list. Used so a member can discover its group's coordinated state set.
 *
 * BOUNDARY-AWARE: the walk stops at (and does not climb past) a group boundary —
 * an object carrying the `stateGroup` marker owns an INDEPENDENT group, so its
 * subtree coordinates with IT, never with a group above it. Concretely: once the
 * walk reaches a boundary, that boundary is the answer if it has states, else the
 * result is null (the boundary shadows any outer group). A boundary with no
 * states yet still shadows — an outer group must not adopt a nested owner's
 * subtree just because the owner hasn't added its first state.
 */
export function nearestStateAncestor(obj: SceneObject): Properties | null {
  let cur: SceneObject | null = obj;
  while (cur) {
    const p = ownProperties(cur);
    if (p && p.getStateNames().length > 0) return p;
    // A boundary shadows everything above it: an object carrying the marker owns
    // an INDEPENDENT group, so its subtree never coordinates with a group higher
    // up. Once the walk reaches a boundary — including `obj` itself — it stops. If
    // that boundary had states we already returned it above; reaching here means
    // it is stateless, so the answer is null (no coordinated group), NOT the outer
    // group. Without this, a stateless nested owner (a just-opened axis with no
    // state added yet) would be adopted as a member of the outer group.
    if (isGroupBoundary(cur)) return null;
    cur = cur.getParent();
  }
  return null;
}

/**
 * The OWNER of the state scope `obj` sits in: the TOPMOST self-or-ancestor
 * Properties owning states, never climbing past a `stateGroup` boundary.
 *
 * Ownership dual of {@link nearestStateAncestor}. Every participating member
 * carries its own seeded copy of the group's state names (the R2 invariant,
 * STATE_SCOPE_SPEC.md), so "nearest stateful ancestor" finds a MEMBER whenever
 * the walk starts inside a member's subtree — e.g. a subasset under a posed
 * model. Orchestrating from a member under-fans: only its subtree switches.
 * The scope owner is the topmost stateful node within the boundary: the first
 * boundary reached wins if it is stateful (it owns everything below), else the
 * highest stateful node seen beneath it.
 *
 * Same shadowing contract as `nearestStateAncestor` — a stateless boundary
 * still caps the walk (the topmost stateful node found BELOW it, or null), and
 * the two walks agree on null-ness; they differ only in WHICH Properties they
 * return (owner vs nearest member).
 *
 * FOREIGN-AXIS STOP: "topmost wins" is only valid while the chain looks like
 * one coordinated group — every stateful node sharing the group's state names
 * (which R2 seeding guarantees for members). A stateful ancestor whose name
 * set is DISJOINT from the candidate's is a different axis, not the owner: a
 * rig-less self-owned stateful object dragged or imported into the group never
 * gets the `stateGroup` marker, so only the name test can tell it from a
 * member. The walk stops there, keeping the candidate — matching the old
 * nearest behavior for foreign axes while climbing through seeded members. An
 * unmarked axis that OVERLAPS the group's names is indistinguishable from a
 * member and resolves as one (join-by-default); the marker is the escape hatch.
 */
export function stateScopeOwner(obj: SceneObject): Properties | null {
  let owner: Properties | null = null;
  let cur: SceneObject | null = obj;
  while (cur) {
    const p = ownProperties(cur);
    if (p && p.getStateNames().length > 0) {
      if (owner && !sharesStateName(owner, p)) break; // foreign axis above — stop
      owner = p;
    }
    // The boundary check comes AFTER the stateful check, mirroring
    // nearestStateAncestor's ordering: a stateful boundary is itself the owner.
    if (isGroupBoundary(cur)) break;
    cur = cur.getParent();
  }
  return owner;
}

/** True when `a` and `b` declare at least one common state name. */
function sharesStateName(a: Properties, b: Properties): boolean {
  const bNames = new Set(b.getStateNames());
  return a.getStateNames().some((n) => bNames.has(n));
}

/**
 * The group `obj` is a member OF — the scope OWNER strictly above it (excluding
 * `obj` itself). A seeded member always carries its own states, so a
 * self-inclusive walk would wrongly report it as its own group; this variant
 * answers "is there a stateful group ABOVE me", and resolves it to the group's
 * OWNER, not the nearest seeded member on the way up (see {@link
 * stateScopeOwner} — landing states on a member's copy would diverge the
 * group's state sets).
 *
 * BOUNDARY-AWARE: a boundary owner is a member of NOTHING above it — its group is
 * independent — so this returns null when `obj` itself carries the marker,
 * regardless of any outer group. (Above `obj`, `stateScopeOwner`'s own boundary
 * stop handles the walk.)
 *
 * Exported for `resolveContext` (stateContext.ts), whose rule 1 is exactly this
 * question — one implementation so the panel context cannot drift from the
 * add/target routing here.
 */
export function nearestStrictAncestorOwner(obj: SceneObject): Properties | null {
  if (isGroupBoundary(obj)) return null;
  const parent = obj.getParent();
  return parent ? stateScopeOwner(parent) : null;
}

/**
 * READ-ONLY owner resolution: the object that owns — or would own — states for
 * `obj`, creating nothing. Mirrors `resolveOrCreateStateOwner`'s ordering:
 *
 *   1. A stateful STRICT ancestor (the group `obj` is a member of) — checked
 *      before self because a fan-out-seeded member carries its own copy of the
 *      group's states and a self-first walk would wrongly promote it.
 *   2. `obj` itself when it owns states (legacy stateful object / existing rig).
 *   3. An existing stateless rig scaffold directly above `obj`, whether it
 *      wraps `obj` or a sibling — that is where a first state WOULD land.
 *   4. `obj` itself (provisional owner; a first state wraps it in a rig).
 *
 * Exported so host UIs (states-panel targeting, add-object context parenting)
 * share one walk instead of re-implementing it (the sandbox overlay's
 * `resolveOwner` and TraceFrontend's states use of `resolvePrimaryParent` are
 * the duplicates this replaces).
 */
export function resolveStateOwner(obj: SceneObject): SceneObject {
  const above = nearestStrictAncestorOwner(obj);
  if (above) return above.sceneObject;
  if ((ownProperties(obj)?.getStateNames().length ?? 0) > 0) return obj;
  return rigScaffoldAbove(obj) ?? obj;
}

/**
 * The rig directly above `obj` that a first state should land on: `obj`'s own
 * rig, or a marked rig scaffold wrapping a sibling. A plain group that only
 * ends in " Rig" doesn't count, and a sibling that is itself a boundary keeps
 * its own states.
 */
function rigScaffoldAbove(obj: SceneObject): SceneObject | null {
  const parent = obj.getParent();
  if (!parent) return null;
  if (hasRigContainer(obj)) return parent;
  if (isGroupBoundary(obj)) return null;
  return isRigContainer(parent) && isGroupBoundary(parent) ? parent : null;
}

/**
 * Resolve the owner that should carry a state for `obj`. LAZY-RIG model: adding
 * a state never creates a rig — a rig appears only when a TRANSLATION is keyed
 * into a rig-less owner's state ({@link setStateTransform} → {@link
 * promoteToRigOwner}) or when content joins the state context ({@link
 * ensureStateContextContainer}). Ordering mirrors {@link resolveStateOwner}:
 *
 *   1. There is a stateful group ABOVE `obj` (strict ancestor) → JOIN it: that
 *      owner carries the state, `obj` is seeded as a member. Checked FIRST —
 *      a fan-out-seeded member carries its own copy of the group's states, so
 *      the self check below would wrongly promote it to its own owner (the
 *      exact trap `nearestStrictAncestorOwner`'s doc describes) and the group's
 *      state sets would diverge. Join-not-nest also applies to nested owners.
 *   2. `obj` itself already owns states (and nothing above does) → it is the
 *      owner: a legacy stateful object, an existing rig, or a lazily-unwrapped
 *      self-owned object.
 *   3. A stateless rig SCAFFOLD sits directly above, wrapping `obj` or a
 *      sibling (created by the add-object trigger before any state existed)
 *      → states land on the rig.
 *   4. No group anywhere in the chain → `obj` is its OWN owner (self-owned
 *      states; portable for value/visibility/rotation/scale overrides, and a
 *      rig materializes on the first keyed translation).
 *
 * Any commands produced along the way (Properties add, member seed) are
 * RETURNED in `preCommands` — not pushed — so the caller bundles them into the
 * same undo entry as the state edit and one Ctrl+Z reverts the whole gesture.
 */
async function resolveOrCreateStateOwner(
  obj: SceneObject,
): Promise<{ owner: SceneObject; preCommands: Array<UndoCommand | null> } | null> {
  // (1) A stateful group above → join it; obj is a member, the group is owner.
  const ancestorOwner = nearestStrictAncestorOwner(obj);
  if (ancestorOwner) {
    const ensured = await ensureStatesObjectCommand(obj);
    const seedCmd = ensured
      ? await seedMemberStatesFromGroup(obj, ensured.props, { record: false })
      : null;
    return { owner: ancestorOwner.sceneObject, preCommands: [ensured?.addCmd ?? null, seedCmd] };
  }

  // (2) obj already owns states (and no group above) → it's the owner.
  const own = ownProperties(obj);
  if (own && own.getStateNames().length > 0) {
    return { owner: obj, preCommands: [] };
  }

  // (3) A stateless rig scaffold above → states belong on the rig (it is where
  // a group already materialized, matching resolveStateOwner's ordering).
  const rig = rigScaffoldAbove(obj);
  if (rig) {
    const ensured = await ensureStatesObjectCommand(rig);
    if (!ensured) return null;
    return { owner: rig, preCommands: [ensured.addCmd] };
  }

  // (4) No group anywhere → obj owns its own states. No rig is created here.
  const ensured = await ensureStatesObjectCommand(obj);
  if (!ensured) return null;
  return { owner: obj, preCommands: [ensured.addCmd] };
}

// --- Lazy rig promotion ------------------------------------------------------

/** Full authored local pose, resilient to an ACTIVE state posing the object:
 * while an override drives the node, authored truth lives in the Properties
 * saved baseline (`getAuthoredTransform`), not raw sceneData. */
function authoredPoseOf(obj: SceneObject, props: Properties | null): ResolvedPose {
  const saved = props?.getAuthoredTransform();
  if (saved) {
    return {
      position: [saved.position.x, saved.position.y, saved.position.z],
      rotation: [saved.rotation.x, saved.rotation.y, saved.rotation.z, saved.rotation.w],
      scale: [saved.scale.x, saved.scale.y, saved.scale.z],
    };
  }
  return authoredLocalPose(obj);
}

const POSE_EPS = 1e-5;

function composePose(p: readonly number[], r: readonly number[], s: readonly number[]): Matrix {
  return Matrix.Compose(
    new Vector3(s[0], s[1], s[2]),
    new Quaternion(r[0], r[1], r[2], r[3]),
    new Vector3(p[0], p[1], p[2]),
  );
}

/**
 * Re-express a state's transform override from the OLD parent frame into the
 * frame of the new rig (whose local pose in that same old frame is `rigPose`).
 * Missing components inherit the authored pose (= `rigPose`, since the rig
 * adopted exactly the object's authored placement) BEFORE the change of frame;
 * afterwards, components that land ≈ identity are OMITTED again — post-wrap the
 * object's authored pose IS identity, so omission both minimizes the data and
 * keeps the component base-following.
 */
function reframeOverrideIntoRig(override: TransformOverride, rigPose: ResolvedPose): TransformOverride {
  // A bound component has no value to change frames with — reframing it would
  // mean resolving it now and freezing the result, which is exactly the
  // binding's opposite. Leave such an override alone.
  if (hasBoundComponent(override)) return override;
  const abs = composePose(
    numericTuple(override.position) ?? rigPose.position,
    numericTuple(override.rotation) ?? rigPose.rotation,
    numericTuple(override.scale) ?? rigPose.scale,
  );
  const rig = composePose(rigPose.position, rigPose.rotation, rigPose.scale);
  // Babylon node composition: world = local × parentWorld, so the new local is
  // abs × rig⁻¹.
  const rel = abs.multiply(Matrix.Invert(rig));
  const scl = new Vector3();
  const rot = new Quaternion();
  const pos = new Vector3();
  rel.decompose(scl, rot, pos);

  const out: TransformOverride = {};
  if (Math.abs(pos.x) > POSE_EPS || Math.abs(pos.y) > POSE_EPS || Math.abs(pos.z) > POSE_EPS) {
    out.position = [pos.x, pos.y, pos.z];
  }
  const IDENT_Q = [0, 0, 0, 1];
  if ([rot.x, rot.y, rot.z, rot.w].some((v, i) => Math.abs(v - IDENT_Q[i]) > POSE_EPS)) {
    out.rotation = [rot.x, rot.y, rot.z, rot.w];
  }
  if (Math.abs(scl.x - 1) > POSE_EPS || Math.abs(scl.y - 1) > POSE_EPS || Math.abs(scl.z - 1) > POSE_EPS) {
    out.scale = [scl.x, scl.y, scl.z];
  }
  return out;
}

/**
 * Promote a rig-less SELF-OWNED stateful object to a rig-owned group: wrap it
 * in a rig at its AUTHORED pose, hoist its state list (names + transitions +
 * `initialState`) onto the rig, and re-express any existing transform overrides
 * into the rig's frame. After promotion the object is an ordinary MEMBER — its
 * keyed poses are rig-relative and the no-owner-keying rule holds for the rig.
 *
 * Triggers: a keyed TRANSLATION on the owner ({@link setStateTransform}) or
 * content joining the context ({@link ensureStateContextContainer}). Returns
 * `{ rig, commands }` for the caller to bundle (never pushes); `null` when the
 * object isn't a rig-less self-owned owner or the wrap is declined (a headless
 * host, or a Pages container's direct child).
 *
 * The object's `initialState` is intentionally LEFT on the member too: each
 * Properties resolves its own initial state at load, so the duplicate is
 * behavior-neutral, and removing a field through a shallow-merge commit is not
 * expressible.
 */
export async function promoteToRigOwner(
  obj: SceneObject,
): Promise<{ rig: SceneObject; commands: Array<UndoCommand | null> } | null> {
  return withGuard(obj, null, async () => {
    const props = ownProperties(obj);
    if (!props || props.getStateNames().length === 0) return null; // not self-owned stateful
    if (nearestStrictAncestorOwner(obj)) return null; // already a member of a group

    const dataBefore = props.getData();

    // Wrap at the authored pose — or adopt an existing scaffold rig above
    // (defensive: both current callers filter the has-rig case, but this is a
    // public export and legacy data can carry self-owned states under a rig).
    let wrapCmd: UndoCommand | null = null;
    let rig = hasRigContainer(obj) ? obj.getParent() : null;
    if (!rig) {
      wrapCmd = await wrapInRigContainer(obj);
      if (!wrapCmd) return null; // wrap declined — nothing was mutated
      // The wrap HAS executed past this point: any bail here would leak the
      // structural change out of the caller's undo bundle. The object's parent
      // is the freshly-created container by construction (a successful wrap
      // ends with reparentSceneObject(obj, container)) — do not re-derive it
      // through name-matching predicates, which have failure modes (the deduped
      // "<name> (N) Rig" once broke here and left a stateless rig behind).
      rig = obj.getParent();
      if (!rig) {
        // Unreachable after a successful wrap; revert rather than leak.
        await wrapCmd.undo();
        return null;
      }
    }
    // The frame the overrides move into. A fresh rig is placed from the
    // authored data alone; a Base `transform` (a bound axis, say) stays on the
    // object, inside the rig, so it is no part of that frame.
    const rigPose = wrapCmd ? authoredLocalPose(rig) : authoredPoseOf(obj, props);

    // Hoist the state LIST onto the rig: names + transitions (+ initialState).
    const ensured = await ensureStatesObjectCommand(rig);
    if (!ensured) {
      // Same revert-never-leak rule as the !rig branch above: a bail after the
      // wrap executed must undo it, or the structural change escapes the
      // caller's undo bundle. (Defensive — ensureStatesObjectCommand only
      // fails without a Properties registry.)
      await wrapCmd?.undo();
      return null;
    }
    let rigData = ensured.props.getData();
    for (const [name, def] of Object.entries(dataBefore.states ?? {})) {
      if (!rigData.states?.[name]) {
        rigData = statesModel.addStateMirroringTransition(rigData, name, def.transition);
      }
    }
    const rigPatch: Partial<PropertiesData> = { states: rigData.states };
    if (dataBefore.initialState !== undefined) rigPatch.initialState = dataBefore.initialState;
    const rigCmd = await buildPropsCommand(rig, rigPatch);

    // Re-express the member's existing transform overrides into the rig frame
    // (they were authored in the OLD parent frame; the frame just changed).
    let memberData = ownProperties(obj)?.getData() ?? dataBefore;
    let reframed = false;
    for (const [name, def] of Object.entries(memberData.states ?? {})) {
      if (!def.transform) continue;
      memberData = statesModel.setStateTransform(memberData, name, reframeOverrideIntoRig(def.transform, rigPose));
      reframed = true;
    }
    const memberCmd = reframed
      ? await buildPropsCommand(obj, { states: memberData.states })
      : null;

    // The rig, the new resolution target for editTargetState, shows what the
    // member shows; the member's commits kept its own states.
    showAsGroup(ensured.props, props);

    Logger.log(`[statesActions] Promoted ${obj.name} to rig-owned group (${rig.name})`);
    return { rig, commands: [wrapCmd, ensured.addCmd, rigCmd, memberCmd] };
  });
}

/**
 * Turn `obj` into the OWNER of a fresh, INDEPENDENT nested state group — the
 * explicit "Separate states" gesture (STATE_GROUP_BOUNDARIES.md Phase 3). Unlike
 * "+ State" (which JOINS an ancestor group), this makes `obj` a boundary so its
 * states are independent of any group it sits inside.
 *
 * A pure marker flip: ensure Properties, set `stateGroup: true`, prune the
 * seed states the outer group fanned in (keeping real overrides), and seed a
 * first state when none survive. No rig, no state migration.
 *
 * Pushes the whole gesture as one undo entry and returns `{ owner, name }`
 * (owner === `obj`, the new inner owner, and the first of its states), or
 * `null` when `obj` is the scene root or already a boundary, or the marker
 * commit is rejected.
 */
export async function createNestedStateGroup(
  obj: SceneObject,
): Promise<{ owner: SceneObject; name: string } | null> {
  return withGuard(obj, null, async () => {
    if (obj === obj.traceScene.getSceneRoot()) return null;
    if (isGroupBoundary(obj)) return null; // already owns an independent group

    const commands: Array<UndoCommand | null> = [];

    // The OUTER group's state names, captured BEFORE the marker flips (after it,
    // the boundary-aware walk reports no outer group). These scope the prune
    // below: only states the outer group could have fanned in are seed
    // candidates — an empty state with a name the outer group does NOT own is
    // the user's own authoring and must survive (the untoggle→retoggle
    // round-trip must not eat it).
    const outerOwner = nearestStrictAncestorOwner(obj);
    const outerNames = new Set(outerOwner?.getStateNames() ?? []);

    // States live PORTABLY on each object already — "separate" is just a marker
    // flip: flip `stateGroup` on and the object's states stop coordinating with
    // the parent group and stand alone. No rig, no state migration. Ensure
    // Properties first (a bare object may have none).
    const ensured = await ensureStatesObjectCommand(obj);
    if (!ensured) return null;
    const markCmd = await buildPropsCommand(obj, { stateGroup: true });
    if (!markCmd) {
      // The marker commit was rejected: take back the Properties add, if any,
      // silently (no selection, no event: the add announced nothing either),
      // so nothing changed.
      await ensured.addCmd?.undo({ silent: true });
      return null;
    }
    commands.push(ensured.addCmd, markCmd);

    // Prune the seed scaffolding the outer group fanned into this object — a keyed
    // pose/visibility that merely echoes its base. SAME prune as ungroup (shared
    // `pruneSeedStates`), so a separated object keeps only its REAL overrides, not
    // inherited placeholders. Scoped to the outer group's names; with no outer
    // group there are no seeds and nothing is pruned.
    if (outerNames.size > 0) commands.push(await pruneSeedStates(obj, outerNames));

    // If nothing real survived (a fresh/undiverged object), seed a first state so
    // there is something to edit — the same bootstrap "+ State" does.
    const props = ownProperties(obj)!;
    let name: string;
    if (props.getStateNames().length === 0) {
      const seeded = statesModel.addInitialStates(props.getData());
      commands.push(await buildPropsCommand(obj, {
        states: seeded.data.states,
        initialState: seeded.data.initialState,
      }));
      name = seeded.name;
    } else {
      name = props.getStateNames()[0];
    }

    Logger.log(`[statesActions] Separated ${obj.name} into its own state group`);
    pushBundle('Separate states', commands);
    return { owner: obj, name };
  });
}

/**
 * INVERSE of {@link createNestedStateGroup} — the "Separate states" toggle OFF.
 * A pure marker flip: clear `stateGroup` so the object's states re-coordinate with
 * the group it sits inside (or it becomes a plain standalone owner when there is
 * none). The states themselves are UNTOUCHED — they sit portably on the object
 * either way; only the coordination marker changes. Pushes one undo entry and
 * returns true; false when `obj` is not a boundary or the marker commit is
 * rejected.
 *
 * Does NOT touch rigs or the hierarchy — un-separating is not un-rigging. A rig is
 * a real structural node that may hold multiple children and is dissolved by its
 * own dedicated flows (delete-object child promotion, last-state demotion), never
 * as a side effect of this marker flip. Toggling the marker off ON a rig simply
 * makes the rig's states rejoin its parent group's coordination; the rig node
 * stays.
 */
export async function dissolveNestedStateGroup(obj: SceneObject): Promise<boolean> {
  return withGuard(obj, false, async () => {
    if (!isGroupBoundary(obj)) return false;
    if (!ownProperties(obj)) return false;
    const cmd = await buildPropsCommand(obj, { stateGroup: false });
    if (!cmd) return false; // the commit was rejected; nothing changed
    Logger.log(`[statesActions] Rejoined ${obj.name} to its parent state group`);
    pushBundle('Rejoin state group', [cmd]);
    return true;
  });
}

/**
 * AUTO-SEPARATE ON GROUP ENTRY — the data-side mirror of {@link
 * dropStatesOnUngroup}. When a stateful object whose axis is FOREIGN to the
 * receiving group (no shared state names) moves into that group's subtree,
 * mark it `stateGroup: true`, making its independence explicit in authored
 * data instead of inferred per-walk by {@link stateScopeOwner}'s foreign-axis
 * heuristic. Explicit data is what other clients (Unity, TF) read, and it
 * gives the object rule-0 context resolution — selecting IT (not just its
 * children) shows its own states.
 *
 * Returns the marker command UNPUSHED (same contract as dropStatesOnUngroup)
 * so a caller that owns a reparent gesture can bundle it. Null when there is
 * nothing to mark: the object is stateless (a plain join), already a
 * boundary, not inside a stateful group, or shares a state name with the
 * receiving owner (same axis → member, join-by-default).
 */
export async function markIndependentOnGroupEntry(obj: SceneObject): Promise<UndoCommand | null> {
  const props = ownProperties(obj);
  if (!props || props.getStateNames().length === 0) return null;
  if (isGroupBoundary(obj)) return null;
  const receiving = nearestStrictAncestorOwner(obj);
  if (!receiving) return null;
  if (sharesStateName(props, receiving)) return null;
  const cmd = await buildPropsCommand(obj, { stateGroup: true });
  if (cmd) Logger.log(`[statesActions] Separated ${obj.name} on entering ${receiving.sceneObject.name}'s group (foreign axis)`);
  return cmd;
}

/**
 * Watch a scene's live reparents and run {@link markIndependentOnGroupEntry}
 * on every moved object, pushing the marker as its own undo entry. Hosts
 * install this once per TraceScene (sandbox: next to the reference-identity
 * registry). Loads never fire it — construction-time parenting bypasses the
 * reparent signal by design (see TraceScene.onObjectReparented) — so opening
 * a scene cannot dirty it; only live gestures mark.
 *
 * Deferred a microtask so the move (dedup, topology heals, any bundled
 * commands) settles before authored data is read. Skipped during undo/redo
 * replays (replaying a reparent must not mint fresh undo entries — the
 * original gesture's entry already carries the marker) and in play mode
 * (script-driven reparents must not mutate authored data).
 */
export function installAutoSeparateOnGroupEntry(scene: TraceScene): () => void {
  return scene.onObjectReparented((obj) => {
    if (scene.isPlayMode() || getUndoManager().isUndoRedoing) return;
    queueMicrotask(() => {
      // Re-check at execution time — the event fired mid-gesture and an
      // undo/redo or play toggle can land between the event and the microtask.
      if (scene.isPlayMode() || getUndoManager().isUndoRedoing) return;
      if (obj.transform?.isDisposed?.()) return;
      markIndependentOnGroupEntry(obj)
        .then((cmd) => { if (cmd) getUndoManager().push(cmd); })
        .catch((err) => Logger.warn('[statesActions] auto-separate on group entry failed:', err));
    });
  });
}

/**
 * The container that new content should be parented under when added into an
 * open state context whose owner is `owner` — creating a rig LAZILY when the
 * owner can't reasonably parent peer content itself:
 *
 *   - an existing container (a rig, or anything that already has children) is
 *     its own parent target — no new rig (legacy groups keep their behavior);
 *   - a member directly under a rig re-targets to that rig;
 *   - a rig-less stateful LEAF is promoted ({@link promoteToRigOwner});
 *   - a rig-less stateless leaf (an opened-but-bare context) gets a scaffold
 *     wrap — the rig exists structurally, states land on it later (see
 *     `resolveOrCreateStateOwner` case 3).
 *
 * Returns the container plus any creation commands for the caller to bundle
 * with its CreateObjectCommand (one undo entry for wrap + create). Falls back
 * to `owner` itself, with no commands, when the wrap is declined or the edit
 * is refused in Play.
 */
export async function ensureStateContextContainer(
  owner: SceneObject,
): Promise<{ container: SceneObject; commands: Array<UndoCommand | null> }> {
  if (isRigContainer(owner) || owner.getChildren().length > 0) {
    return { container: owner, commands: [] };
  }
  if (hasRigContainer(owner)) {
    const rig = owner.getParent();
    if (rig) return { container: rig, commands: [] };
  }
  return withGuard(owner, { container: owner, commands: [] }, async () => {
    if ((ownProperties(owner)?.getStateNames().length ?? 0) > 0) {
      const promo = await promoteToRigOwner(owner);
      if (promo) return { container: promo.rig, commands: promo.commands };
    } else {
      const wrapCmd = await wrapInRigContainer(owner);
      const rig = owner.getParent();
      if (wrapCmd && rig) return { container: rig, commands: [wrapCmd] };
    }
    return { container: owner, commands: [] };
  });
}

// --- 5/6. Seeding -----------------------------------------------------------

/**
 * Seed an object's Properties with the state NAMES of its nearest group ancestor
 * (empty StateDefinitions), then activate the group's current state. No-op when
 * the object already has states or has no stateful group ancestor. `props`
 * defaults to `obj`'s own Properties (it must always be that — the param is
 * internal convenience, not a second target).
 *
 * `record: false` builds the seed command and RETURNS it (without pushing) so a
 * caller that immediately follows the seed with an edit can bundle both into one
 * undo entry — otherwise an inline seed-then-edit leaves two undo steps and the
 * first Ctrl+Z lands on a "seeded but un-edited" state the user never created.
 * Default (`record` unset/true) pushes its own command, for standalone seeding.
 * Returns the seed command when `record: false`, else null. Refused in Play, it
 * seeds nothing and returns null.
 */
export async function seedMemberStatesFromGroup(
  obj: SceneObject,
  props = ownProperties(obj),
  options: { record?: boolean } = {},
): Promise<UndoCommand | null> {
  if (!props || props.getStateNames().length > 0) return null;
  // Mirror the OWNER's set, not the nearest seeded member's copy — the caller
  // (resolveOrCreateStateOwner) just declared the owner via the same walk, and
  // a member's mirror can be drifted (missed rename fan-out) or show another
  // state than the group's (a runtime write on the member).
  // `obj` is stateless here (guard above), so the self-inclusive walk is safe.
  const group = stateScopeOwner(obj);
  if (!group || group === props) return null;
  const names = group.getStateNames();
  if (names.length === 0) return null;

  return withGuard(obj, null, async () => {
    // Read the group's data INSIDE the guard (consistent with `props.getData()`
    // below) so it can't be a stale snapshot relative to queued actions.
    const groupData = group.getData();
    let data = props.getData();
    for (const n of names) {
      if (!(n in (data.states ?? {}))) {
        // Mirror the GROUP state's transition EXACTLY (custom, default, or cleared/
        // snap) so the member animates identically to the owner. A member of a snap
        // group must snap too — not bake the 0.3s default.
        data = statesModel.addStateMirroringTransition(data, n, groupData.states?.[n]?.transition);
      }
    }
    const patch = { states: data.states } as Partial<PropertiesData>;
    let command: UndoCommand | null = null;
    if (options.record === false) command = await buildPropsCommand(obj, patch);
    else await commitProps(obj, patch);
    const member = ownProperties(obj);
    if (member) showAsGroup(member, group);
    return command;
  });
}

/**
 * Pre-seed a group member before an edit and prime its resolver cache so a
 * drag-end can't leak into Base. Seeds if unseeded; otherwise shows the group's
 * state on it when the member shows another.
 *
 * Records NO undo entries. Hosts call this from reactive selection/projection
 * effects (TF's useStates/TransformSection fire it whenever a group member is
 * selected), and a reactive caller pushing history is a self-refilling undo
 * entry: undoing the seed commit fires behavior-changed → the host re-projects
 * → the effect re-fires → re-seed pushes a fresh entry — the stack top never
 * advances and every entry beneath is unreachable (TF #2094 QA "undo frozen").
 * The seed is derived data (empty per-state defs mirroring the group), so it is
 * safe to materialize silently; edit actions that need an UNDOABLE seed bundle
 * it themselves via `seedMemberStatesFromGroup(..., { record: false })`.
 *
 * @deprecated No host calls this anymore (2026-07): TF removed its reactive
 * pre-seed — everything this did happens inline at edit time
 * (`setStateTransform` seeds + primes the baseline; hosts' projection heals
 * cover diverged members). Reactive callers were also the source of two bug
 * classes (TF #2094 undo freeze; the member switch-tween stomp). Dropped from
 * the public `trace-viewer/editor` surface; retained internally only as the
 * seeding primitive the states-action tests use (and its no-undo-entry
 * contract test). Delete outright once those tests seed via
 * `seedMemberStatesFromGroup` instead.
 */
export async function ensureMemberSeededForEdit(obj: SceneObject): Promise<void> {
  if (obj.transform?.isDisposed?.()) return; // reactive callers can hold a stale ref
  const group = nearestStateAncestor(obj);
  if (!group) return; // standalone — nothing to coordinate
  await withGuard(obj, undefined, async () => {
    const ensured = await ensureStatesObjectCommand(obj); // addCmd discarded — silent
    if (!ensured) {
      Logger.warn(`[statesActions] could not ensure Properties on ${obj.name} for edit seeding`);
      return;
    }
    const props = ensured.props;
    if (props.getStateNames().length === 0) {
      // record: false builds (and commits) the seed without pushing; the
      // returned command is deliberately dropped — see the header.
      await seedMemberStatesFromGroup(obj, props, { record: false });
    } else {
      // Already seeded — re-prime the resolver to the GROUP's active state,
      // but ONLY if it actually diverged (the member shows another state
      // than the group). Two fixes folded together:
      //  - Resolve the group via the STRICT ancestor: for a seeded member,
      //    `nearestStateAncestor(obj)` is the member ITSELF (it carries its own
      //    copy of the states), so the old re-prime was self-referential.
      //  - Idempotency: hosts call this from reactive effects that re-fire on
      //    every card click; an unconditional INSTANT re-assert of the state
      //    the member is ALREADY in recomputes and snaps the transform,
      //    cancelling the in-flight switch tween — group members jump to their
      //    state instead of easing. Skipping the no-op makes reactive calls safe.
      const group = nearestStrictAncestorOwner(obj);
      const groupActive = group?.getActiveState();
      if (group && groupActive !== undefined && props.getActiveState() !== groupActive) {
        showAsGroup(props, group);
      }
    }
  });
}

// --- 7. Routing -------------------------------------------------------------

/**
 * The state name ANY authored edit (transform, visibility, or property value)
 * should target: the group's active state for a member, the object's own active
 * state when standalone. Returns undefined when the active state is Base (the edit
 * bakes into the node's authored data instead of a state override).
 *
 * GROUP-AUTHORITATIVE (hoisted from TF's `activeStateForValueEdit`): for a
 * member, the STRICT-ANCESTOR group's active state is the routing truth — the
 * member's own active stack is just a mirror, and intervening Properties
 * commits mid-flow (a declaration ensure, a Renderer slot bind) reset the
 * member's resolver to Base. A self-inclusive walk would stop at the seeded
 * member, read that dropped stack as Base, and leak the edit into authored
 * data. The group's stack survives the member's commits. Only a standalone /
 * self-owned object (no stateful ancestor) reads its own active state.
 *
 * BOUNDARY-AWARE via `nearestStrictAncestorOwner`: a boundary owner is a member of
 * nothing above it, so that call returns null and the edit routes into the
 * boundary's OWN active state — e.g. with an ancestor's "layout" state active,
 * editing a color on a boundary model captures into the model's active color
 * state (or Base), never into the layout axis.
 */
export function editTargetState(obj: SceneObject): string | undefined {
  const group = nearestStrictAncestorOwner(obj);
  const props = group ?? ownProperties(obj);
  if (!props) return undefined;
  const active = props.getActiveState();
  // Base (undefined active, or a pinned base) → edits land on authored data.
  if (active === undefined) return undefined;
  if (statesModel.isBaseState(props.getData(), active)) return undefined;
  return active;
}

/**
 * The state a TRANSFORM edit (gizmo drag, numeric transform field) should
 * target — {@link editTargetState} with one owner carve-out: dragging a RIG
 * CONTAINER always edits the group's PLACEMENT (authored pose), never a state.
 * The rig is the stable frame its members' state poses are relative to; keying
 * its pose would pin the whole group to a spot (the portability bug rigs exist
 * to fix) — and post-lazy-rigs the self-owned branch would even promote the
 * rig itself into a nested `<name> Rig Rig`. Hosts route drags through THIS
 * resolver so a rig drag falls back to their authored-fold + TransformCommand
 * path; VALUE and visibility edits keep using `editTargetState` (group-level
 * per-state values on the owner are legitimate).
 */
export function transformEditTargetState(obj: SceneObject): string | undefined {
  if (isRigContainer(obj)) return undefined;
  return editTargetState(obj);
}

// --- 8/9/10. Switch / preview ----------------------------------------------

/**
 * Show `name` on this Properties as the editor's pick, alone: a custom state,
 * a pseudo-state (`:hover`) or a compound (`X:hover`), masking every runtime
 * state; `undefined` picks Base. Editor preview is single-stack-over-Base, so
 * a state can be authored in isolation. (At runtime, pseudo-states stack by
 * priority and are pointer-triggered — unchanged.)
 */
function applyIsolated(p: Properties, name: string | undefined, animate = false): void {
  // An animated switch resolves its transition as the runtime trace/setState
  // does, with no editor fallback, so a state with no transition snaps here as
  // in Play.
  p.setEditorState(name ?? null, !animate);
}

/**
 * Show on a member what its group shows, in the same layer: the runtime
 * slot's state, and the group's pick when it picks a state (a Base pick on a
 * group leaves its members to their runtime slot).
 */
function showAsGroup(member: Properties, group: Properties): void {
  member.setState(group.getRuntimeState(), true);
  const pick = group.getEditorState();
  if (pick) member.setEditorState(pick, true);
}

/**
 * Apply `name` to `obj` and every GROUP descendant that carries it, in ISOLATION,
 * as the editor's pick (see `applyIsolated`). `undefined` = Base. Instant by default;
 * pass `animate: true` to play each state's transition (duration-based switch
 * preview).
 *
 * Descendants default to {@link groupDescendants} — a nested group boundary and
 * its subtree are PRUNED, so switching this group (including to Base) never
 * knocks an independent inner group out of its own active state. Callers that
 * pass an explicit `descendants` list are responsible for pruning it (all in-repo
 * callers pass a `groupDescendants` result).
 */
export function applyStateToSubtree(
  obj: SceneObject,
  name: string | undefined,
  descendants: readonly SceneObject[] = groupDescendants(obj),
  animate = false,
): void {
  const apply = (o: SceneObject) => {
    const p = ownProperties(o);
    if (!p) return;
    // Only objects that carry the name (or Base = undefined) are switched.
    if (name === undefined || p.getStateNames().includes(name)) applyIsolated(p, name, animate);
  };
  apply(obj);
  for (const d of descendants) apply(d);
}

/**
 * Preview a state across the subtree and notify consumers so panels re-read
 * state-bound values. Not an undoable data mutation. `undefined` = Base. Instant
 * by default; pass `animate: true` to play the state's transition (the editor
 * uses this when the user switches states so a duration-based transition shows).
 */
export function switchState(obj: SceneObject, name: string | undefined, animate = false): void {
  // groupDescendants prunes nested group boundaries (an inner group keeps its own
  // active state through an outer switch). Capture it once and share it across the
  // apply and notify passes rather than traversing twice.
  const descendants = groupDescendants(obj);
  applyStateToSubtree(obj, name, descendants, animate);
  const notify = (o: SceneObject) => {
    if (ownProperties(o)) {
      dispatchBehaviorChanged({ objectPath: o.getPath(), behaviorType: 'Renderer', action: 'updated' });
    }
  };
  notify(obj);
  for (const d of descendants) notify(d);
}

/**
 * Preview `name` across the group as an animated editor pick — for previewing
 * a transition. The transition resolves as the runtime trace/setState does, so
 * a state with no transition snaps here as in Play. `undefined` ends the
 * preview: each object's pick clears, back to what its runtime slot shows.
 */
export function previewState(obj: SceneObject, name: string | undefined): void {
  const apply = (o: SceneObject) => {
    const p = ownProperties(o);
    if (!p) return;
    if (name === undefined || p.getStateNames().includes(name)) {
      p.setEditorState(name, false);
    }
  };
  apply(obj);
  for (const d of groupDescendants(obj)) apply(d);
}

// --- 11/17. Per-state value/transform/transition ----------------------------

/**
 * Write a transform override into the active state for `obj`. Routes to the
 * group's active state for a member; seeds the member if needed. No-op when the
 * target is Base.
 *
 * LAZY-RIG owner handling: when `obj` is a rig-less SELF-OWNED owner of the
 * target state, an absolute keyed POSITION would pin the object to a spot (the
 * portability bug rigs exist to fix), so:
 *   - a position that ≈ the authored placement is STRIPPED from the override
 *    (full-pose captures like the sandbox's are normalized here, centrally);
 *   - a position that genuinely differs triggers {@link promoteToRigOwner} —
 *     the rig materializes, the override is re-expressed rig-relative, and the
 *     whole gesture (wrap + hoist + key) is ONE undo entry. A Pages
 *     container's direct child is never wrapped and keys the position as-is;
 *   - rotation/scale-only overrides key WITHOUT a rig — an omitted position
 *     inherits the authored placement, portable by construction.
 * Members of a group are unaffected (their poses are owner-relative already).
 *
 * The promotion, Properties add, member seed and key are one undo entry.
 * `record: false` returns that command instead of pushing it, for a host that
 * bundles it into a larger gesture. Refused in Play, it returns `{ command: null }`.
 */
export async function setStateTransform(
  obj: SceneObject,
  transform: TransformOverride,
  options: { record?: boolean } = {},
): Promise<{ command: UndoCommand | null }> {
  // Owner-drag carve-out (see transformEditTargetState): a rig's pose is the
  // group's placement — never keyed into a state. Hosts route rig drags to
  // authored; this guard covers direct calls (without it, the self-owned
  // branch below would promote the RIG into a nested rig).
  if (isRigContainer(obj)) return { command: null };
  const target = editTargetState(obj);
  if (target === undefined) return { command: null }; // Base — edits go to authored data elsewhere
  return withGuard(obj, { command: null }, async () => {
    let effective = transform;
    const promoteCmds: Array<UndoCommand | null> = [];
    const own = ownProperties(obj);
    const selfOwnedRigless =
      !nearestStrictAncestorOwner(obj)
      && !hasRigContainer(obj)
      && !!own
      && own.getStateNames().includes(target);
    if (selfOwnedRigless) {
      const authored = authoredPoseOf(obj, own);
      if (effective.position && transformsApproxEqual({ position: effective.position }, authored)) {
        const { position: _echo, ...rest } = effective;
        effective = rest;
      }
      if (effective.position) {
        const promo = await promoteToRigOwner(obj);
        if (promo) {
          promoteCmds.push(...promo.commands);
          // Into the rig's frame, not `authored`: that one includes what a
          // Base `transform` adds, which the rig does not carry.
          const reframed = reframeOverrideIntoRig(effective, authoredLocalPose(promo.rig));
          // Merge onto the JUST-REFRAMED existing override for this state: the
          // promotion re-expressed e.g. an earlier keyed rotation into the rig
          // frame, and the model's setStateTransform REPLACES the transform
          // wholesale — passing the new position alone would drop it.
          const existing = ownProperties(obj)?.getData().states?.[target]?.transform;
          effective = { ...existing, ...reframed };
        }
        // promo === null (the wrap was declined: a headless host, or a Pages
        // container's direct child): key as-is, in the current parent's frame.
      } else if (Object.keys(effective).length === 0) {
        return { command: null }; // position echoed authored and nothing else changed — no-op
      }
    }
    // Never null in practice: the ensure returns the existing or a new Properties.
    const ensured = await ensureStatesObjectCommand(obj);
    if (!ensured) return { command: null };
    const props = ensured.props;
    // Adding Properties primes the saved-transform baseline from authored
    // sceneData, so the seed/edit commits below won't leak a gizmo-moved live
    // node into Base.
    let seedCmd: UndoCommand | null = null;
    if (!props.getStateNames().includes(target)) {
      seedCmd = await seedMemberStatesFromGroup(obj, props, { record: false });
    }
    let next = statesModel.setStateTransform(props.getData(), target, effective);
    // Legacy-migration shim: every creation path now bakes a transition (addState /
    // seedMemberStatesFromGroup), so a freshly-seeded member already has one. This
    // only fires for LEGACY data authored before that change — a member state with
    // no transition — backfilling the group's so a moved member animates like the
    // owner instead of snapping. New data no-ops here, so guard the O(depth)
    // ancestor walk on the missing-transition case (skipped entirely for new data).
    //
    // Scoped to the TRANSFORM edit path ON PURPOSE: re-posing a member is the
    // common case that exposes the snap, and it's not data loss either way (a
    // legacy member whose only edit is visibility/value keeps its absent transition
    // = snap, a cosmetic-only mismatch on pre-PR data). We deliberately do NOT
    // triplicate this shim onto setStateActive/captureStateValue.
    if (next.states?.[target]?.transition === undefined) {
      // STRICT owner walk: obj is stateful here (a state transform was just set),
      // so the self-inclusive nearestStateAncestor would return obj's OWN
      // Properties and read back the very transition found missing — the shim
      // never fired for its target case (a legacy member snapping while the
      // owner animates). The group's transition lives on the owner above.
      const groupTransition = nearestStrictAncestorOwner(obj)?.getData().states?.[target]?.transition;
      if (groupTransition !== undefined) {
        next = statesModel.setStateTransition(next, target, groupTransition);
      }
    }
    const editCmd = await buildPropsCommand(obj, { states: next.states });
    const command = bundleCommands('Set state transform', [...promoteCmds, ensured.addCmd, seedCmd, editCmd]);
    if (command && options.record !== false) getUndoManager().push(command);
    return { command: options.record === false ? command : null };
  });
}

/**
 * Route an object's active (visibility) toggle into the active group state, so
 * hiding/showing an object in a state doesn't change its Base visibility. Mirrors
 * setStateTransform. Returns true if it captured into a state (caller skips the
 * authored setActive). Returns false at Base (edit goes to authored data) and
 * when refused in Play; check `editTargetState` to tell them apart, because a
 * refused state edit must not be written to Base instead.
 */
export async function setStateActive(obj: SceneObject, active: boolean): Promise<boolean> {
  if (editTargetState(obj) === undefined) return false; // Base — toggle the authored active
  const command = await stateActiveCommand(obj, active);
  if (!command) return false;
  getUndoManager().push(command);
  return true;
}

/**
 * Write `active` into the state that shows for `obj` (`editTargetState`), and
 * return the command for it, already applied and not pushed. Null at Base and
 * when refused in Play. The state shows on `obj` afterwards.
 */
export async function stateActiveCommand(obj: SceneObject, active: boolean): Promise<UndoCommand | null> {
  const target = editTargetState(obj);
  if (target === undefined) return null;
  return withGuard(obj, null, async () => {
    const ensured = await ensureStatesObjectCommand(obj);
    if (!ensured) return null;
    const props = ensured.props;
    let seedCmd: UndoCommand | null = null;
    if (!props.getStateNames().includes(target)) {
      seedCmd = await seedMemberStatesFromGroup(obj, props, { record: false });
    }
    const next = statesModel.setStateActive(props.getData(), target, active);
    const editCmd = await buildPropsCommand(obj, { states: next.states });
    return bundleCommands('Set state visibility', [ensured.addCmd, seedCmd, editCmd]);
  });
}

/**
 * Route a property-value edit into the active state for `obj`. Mirrors
 * `setStateTransform`: targets the group's active state for a member, seeds the
 * member if needed. Returns true if it
 * captured into a state, false at Base (the caller should write authored data —
 * e.g. the declaration's base value — instead) and when refused in Play. With
 * a state target (`editTargetState`), false means refused: write nothing.
 *
 * `record: false` builds the command and returns it (for atomic bundling) without
 * pushing it onto the undo stack; otherwise one BehaviorCommitCommand is pushed.
 *
 * Named `captureStateValue` (not `setStateValue`) because the barrel already
 * exports the 4-arg pure MODEL `setStateValue(data, stateName, prop, value)` —
 * the historical name collision shadowed this action and forced hosts to
 * re-implement it (TraceFrontend's `writeStateValue`).
 */
export async function captureStateValue(
  obj: SceneObject,
  prop: string,
  value: PropertyValue,
  options: { record?: boolean } = {},
): Promise<{ captured: boolean; command: UndoCommand | null }> {
  const target = editTargetState(obj);
  if (target === undefined) return { captured: false, command: null }; // Base
  return withGuard(obj, { captured: false, command: null }, async () => {
    const ensured = await ensureStatesObjectCommand(obj);
    if (!ensured) return { captured: false, command: null };
    const props = ensured.props;
    // The Properties add and inline seed travel WITH the edit into the undo
    // stack — whether the caller bundles (record:false) or we push here — so
    // one undo reverts all of them.
    let seedCmd: UndoCommand | null = null;
    if (!props.getStateNames().includes(target)) {
      seedCmd = await seedMemberStatesFromGroup(obj, props, { record: false });
    }
    const next = statesModel.setStateValue(props.getData(), target, prop, value);
    const patch = { states: next.states } as Partial<PropertiesData>;
    const editCmd = await buildPropsCommand(obj, patch);
    const commands = [ensured.addCmd, seedCmd, editCmd];
    if (options.record === false) {
      return { captured: true, command: bundleCommands('Set state value', commands) };
    }
    pushBundle('Set state value', commands);
    return { captured: true, command: null };
  });
}

/**
 * Remove `prop`'s override from the active state for `obj` — the state stops
 * overriding it and follows Base again (the "clear/reset-to-base" gesture while
 * a state is active). Mirrors {@link captureStateValue}: targets the group's
 * active state. Returns `cleared: false` at Base
 * (nothing to clear), when the state carries no such override, or when refused
 * in Play.
 *
 * `record: false` builds and returns the command for atomic bundling instead of
 * pushing it.
 */
export async function clearStateValue(
  obj: SceneObject,
  prop: string,
  options: { record?: boolean } = {},
): Promise<{ cleared: boolean; command: UndoCommand | null }> {
  const target = editTargetState(obj);
  if (target === undefined) return { cleared: false, command: null }; // Base
  return withGuard(obj, { cleared: false, command: null }, async () => {
    const props = ownProperties(obj);
    if (!props?.getStateNames().includes(target)) return { cleared: false, command: null };
    const data = props.getData();
    if (!Object.hasOwn(data.states?.[target]?.values ?? {}, prop)) return { cleared: false, command: null };
    const next = statesModel.removeStateValue(data, target, prop);
    const editCmd = await buildPropsCommand(obj, { states: next.states });
    if (options.record === false) {
      return { cleared: true, command: editCmd };
    }
    pushBundle('Clear state value', [editCmd]);
    return { cleared: true, command: null };
  });
}

/**
 * Set or clear a state's transition on a SINGLE object; the commit keeps what it
 * shows, a `:hover`/`:selected` preview included. For a whole group use
 * {@link setStateTransitionForSubtree}.
 */
export async function setStateTransition(
  obj: SceneObject,
  stateName: string,
  transition: TransitionConfig | undefined,
): Promise<void> {
  const props = ownProperties(obj);
  if (!props) return;
  const next = statesModel.setStateTransition(props.getData(), stateName, transition);
  await withGuard(obj, undefined, async () => {
    await commitProps(obj, { states: next.states });
  });
}

/**
 * Set/clear the named state's transition across the group subtree — the owner
 * and every Properties-bearing descendant that carries the state — so a group's
 * transform and each member's per-state overrides animate together. Members
 * without the state are skipped (no implicit seeding).
 */
export async function setStateTransitionForSubtree(
  obj: SceneObject,
  stateName: string,
  transition: TransitionConfig | undefined,
): Promise<void> {
  await setStateTransition(obj, stateName, transition);
  for (const d of groupDescendants(obj)) {
    if (ownProperties(d)?.getStateNames().includes(stateName)) {
      await setStateTransition(d, stateName, transition);
    }
  }
}

// --- 12-15. Fan-out mutations ----------------------------------------------

/**
 * Add a state to the GROUP and key it across the whole subtree.
 *
 * The new state is created with a DEFAULT TRANSITION (baked by addState) and the
 * current ("active") LIVE POSE keyed into every Properties-bearing DESCENDANT —
 * so the state snapshots how the subtree looks right now instead of starting
 * empty. The OWNER's own pose is deliberately NOT keyed: it is the group's
 * placement in its parent frame, and keying it would pin the group to an
 * absolute spot (moving/duplicating the group would snap it back). Members that
 * lack the state are seeded with it; descendants without Properties are skipped
 * (a contribution needs a Properties resolver to land on). A keyed pose that
 * just echoes the authored base is redundant scaffolding and is pruned on
 * ungroup (see `dropStatesOnUngroup`).
 *
 * ## Owner resolution (lazy rigs)
 *
 * The owner is resolved up front (see `resolveOrCreateStateOwner`):
 *   - If `obj` is already inside a stateful group (a rig/owner above it),
 *     that owner gains the new state and the whole group is re-keyed — the
 *     selected object simply participates as a member. No new rig, no sub-group.
 *   - Otherwise `obj` becomes its OWN owner (self-owned states). NO rig is
 *     created by adding a state — a rig materializes lazily, only when a
 *     translation is keyed into the owner's state (`promoteToRigOwner` via
 *     `setStateTransform`) or content joins the context
 *     (`ensureStateContextContainer`).
 *
 * This keeps one joint state system per group (nested adds join, not nest) and
 * makes the owner the thing `nearestStateAncestor` / the states UI target.
 */
export async function addStateForSubtree(
  obj: SceneObject,
): Promise<{ name: string; owner: SceneObject } | null> {
  const result = await withGuard(obj, null, async () => {
    // Resolve the owner (rig) that will carry the state, wrapping on first state.
    const resolved = await resolveOrCreateStateOwner(obj);
    if (!resolved) return null;
    const { owner, preCommands } = resolved;

    const props = ownProperties(owner);
    if (!props) return null;
    const existing = props.getData();
    const first = Object.keys(existing.states ?? {}).length === 0;

    // addInitialStates/addState bake the default transition; we additionally key
    // each MEMBER's live pose into the new state — but NEVER the owner's own.
    // The owner's local transform is the group's placement in its parent frame;
    // keying it into a state pins the group to an absolute spot, so moving or
    // duplicating the group makes the state snap it back to the original
    // location. This applies to ANY owner (rig container or legacy stateful
    // group), so it is unconditional — not gated on rig recognition. Members'
    // poses are owner-relative and stay portable.
    const seeded = first ? statesModel.addInitialStates(existing) : statesModel.addState(existing);
    const stateName = seeded.name;
    // Slim pin for the OWNER too (a self-owned stateful mesh is the primary
    // case): echo already-bound fields' resolved values into the new state.
    // Unbound fields stay lazy: no eager per-slot declarations. It reads the
    // values BEFORE the commit below, which clears the owner's active state:
    // afterwards it would see Base, not what is showing.
    const ownerData = pinBoundIntoState(owner, props, seeded.data, stateName);

    // Owner-resolution commands (wrap / Properties add / member seed) prepend
    // the bundle so redo replays them before re-keying and one Ctrl+Z reverts
    // the whole gesture.
    const commands: Array<UndoCommand | null> = [
      ...preCommands,
      await buildPropsCommand(owner, { states: ownerData.states, initialState: ownerData.initialState }),
    ];

    for (const d of groupDescendants(owner)) {
      const p = ownProperties(d);
      if (!p) continue;
      // Ensure the state exists on the member (addState bakes the transition), then
      // key its live pose AND live visibility into it. Pinning `active` matters
      // because an omitted state `active` FOLLOWS Base ("omitted = no change") —
      // a later Base hide/show would silently leak into the state. Like the pose,
      // a keyed visibility that just echoes authored base is scaffolding and is
      // pruned on ungroup (see `dropStatesOnUngroup`). The OWNER is excluded from
      // this loop entirely — its visibility (like its pose) is the group's
      // Base-driven existence, so hiding the whole group in Base hides it in
      // every state.
      const names = p.getStateNames();
      let memberData = names.includes(stateName) ? p.getData() : statesModel.addState(p.getData(), stateName).data;
      // ...except a position or scale a layout writes, so a later re-solve
      // reaches this state too.
      memberData = statesModel.setStateTransform(memberData, stateName, withoutOwnedFields(d, liveLocalPose(d)));
      memberData = statesModel.setStateActive(memberData, stateName, d.active);
      // Slim material pin: for slots ALREADY bound to a property, echo the
      // currently-resolving value into the new state so a later Base material
      // edit doesn't leak in. NO new declarations/bindings are created — lazy
      // binding is the job of setMaterial*ForState at first authoring.
      memberData = pinBoundIntoState(d, p, memberData, stateName);
      commands.push(await buildPropsCommand(d, { states: memberData.states }));
    }
    pushBundle('Add state', commands);
    return { owner, stateName };
  });
  if (result === null) return null;
  applyStateToSubtree(result.owner, result.stateName);
  // Return the RESOLVED owner alongside the name: the state may live on a rig
  // or ancestor group rather than `obj`, and host UIs must re-point their edit
  // context to the actual owner — re-deriving it from the parent chain is how
  // the outer-group mis-rebase bug happened.
  return { name: result.stateName, owner: result.owner };
}

/** Duplicate a state across the group subtree (each member clones its own version). */
export async function cloneStateForSubtree(obj: SceneObject, fromName: string): Promise<string | null> {
  const name = await withGuard(obj, null, async () => {
    const props = ownProperties(obj);
    if (!props) return null;
    // getData() returns a FRESH structuredClone each call, so the model's
    // input-identity no-op signal must be compared against the SAME snapshot we
    // passed in — never a second getData() (which can never be ===).
    const data = props.getData();
    const cloned = statesModel.cloneState(data, fromName);
    if (cloned.name === fromName && cloned.data === data) return null; // source missing
    const stateName = cloned.name;
    const commands: Array<UndoCommand | null> = [
      await buildPropsCommand(obj, { states: cloned.data.states }),
    ];

    // The owner's clone carries fromName's transition; members that don't have
    // fromName get a fresh state, which must mirror that transition (not the baked
    // default) so the duplicated state animates consistently across the subtree.
    const clonedTransition = cloned.data.states?.[stateName]?.transition;
    for (const d of groupDescendants(obj)) {
      const p = ownProperties(d);
      if (!p) continue;
      const names = p.getStateNames();
      if (names.length === 0) continue;
      const next = names.includes(fromName)
        // Member carries the source state → clone it (copies its transition).
        ? statesModel.cloneState(p.getData(), fromName, stateName).data
        // Fresh state on this member → mirror the owner's cloned transition exactly.
        : statesModel.addStateMirroringTransition(p.getData(), stateName, clonedTransition);
      commands.push(await buildPropsCommand(d, { states: next.states }));
    }
    pushBundle('Duplicate state', commands);
    return stateName;
  });
  if (name === null) return null;
  applyStateToSubtree(obj, name);
  return name;
}

/** Rename a state across the group subtree + repoint interaction refs. */
export async function renameStateForSubtree(obj: SceneObject, oldName: string, newName: string): Promise<boolean> {
  const props = ownProperties(obj);
  if (!props) return false;
  const activeBefore = props.getActiveState();
  const ok = await withGuard(obj, false, async () => {
    // Compare the model result against the SAME snapshot passed in — getData()
    // clones, so a second call could never be === (rejection would never fire).
    const data = props.getData();
    const next = statesModel.renameState(data, oldName, newName);
    if (next === data) return false; // rejected (missing / collision)
    const commands: Array<UndoCommand | null> = [
      await buildPropsCommand(obj, { states: next.states, initialState: next.initialState }),
    ];

    for (const d of groupDescendants(obj)) {
      const p = ownProperties(d);
      if (!p || !p.getStateNames().includes(oldName)) continue;
      const pData = p.getData();
      const dn = statesModel.renameState(pData, oldName, newName);
      if (dn === pData) continue;
      commands.push(await buildPropsCommand(d, { states: dn.states, initialState: dn.initialState }));
    }
    // Scene-wide, target-aware heal of interaction/Pages refs — folded into the
    // SAME undo entry so one Ctrl+Z restores Properties + graphs + pages together.
    commands.push(...await buildStateRefHealCommands(obj, oldName, newName));
    pushBundle('Rename state', commands);
    return true;
  });
  if (!ok) return false;

  // The commits kept every other state; the renamed one shows again under its new name.
  if (activeBefore === oldName) applyStateToSubtree(obj, newName);
  return true;
}

/**
 * Delete a state across the group subtree + redirect dangling refs to Base.
 * Objects left without a state also lose their clip bindings, in the same undo entry.
 */
export async function deleteStateForSubtree(obj: SceneObject, name: string): Promise<boolean> {
  const props = ownProperties(obj);
  if (!props) return false;
  const ok = await withGuard(obj, false, async (): Promise<boolean> => {
    // Same getData()-identity caveat as rename: compare against one snapshot.
    const data = props.getData();
    const next = statesModel.deleteState(data, name);
    if (next === data) return false;
    const commands: Array<UndoCommand | null> = [
      await buildPropsCommand(obj, { states: next.states, initialState: next.initialState }),
    ];
    // Objects this delete left without a state: their clip bindings swap nothing now.
    const emptied: SceneObject[] = Object.keys(next.states ?? {}).length === 0 ? [obj] : [];

    for (const d of groupDescendants(obj)) {
      const p = ownProperties(d);
      if (!p || !p.getStateNames().includes(name)) continue;
      const dn = statesModel.deleteState(p.getData(), name);
      commands.push(await buildPropsCommand(d, { states: dn.states, initialState: dn.initialState }));
      if (Object.keys(dn.states ?? {}).length === 0) emptied.push(d);
    }

    // LAZY-RIG demotion — the inverse of promoteToRigOwner: deleting the LAST
    // state can leave a rig with no reason to exist (it materialized to carry
    // a keyed translation that is now gone). Unwrap the single member back to
    // the rig's slot (world pose preserved) and bundle it into the SAME undo
    // entry, so one Ctrl+Z restores state + rig together. Conservative gates:
    // the rig must carry nothing beyond the (now-empty) states scaffold, and
    // the member must retain no transform-carrying states of its own (those
    // poses are expressed in the rig frame — removing the frame would corrupt
    // them; such extra member-only states shouldn't exist, but legacy data
    // can be weird).
    let rewriteRoot = obj;
    if (Object.keys(next.states ?? {}).length === 0 && isRigContainer(obj)) {
      const kids = obj.getChildren();
      const member = kids.length === 1 ? kids[0] : null;
      const rigBare =
        (next.properties?.length ?? 0) === 0
        && (obj.getSceneObjectData().behaviors ?? []).every((b) => b['@type'] === 'Properties');
      const memberStates = member ? ownProperties(member)?.getData().states ?? {} : {};
      const memberSafe = Object.values(memberStates).every((d) => d.transform === undefined);
      if (member && hasRigContainer(member) && rigBare && memberSafe) {
        const unwrapCmd = await unwrapRigContainer(obj);
        if (unwrapCmd) {
          commands.push(unwrapCmd);
          rewriteRoot = member; // the rig is gone — heal refs against the survivor
        }
      }
    }

    // Objects left stateless have their clip bindings torn down in this entry,
    // before the ref heal. A demoted rig carries only Properties, so none.
    for (const o of emptied) commands.push(...await buildOrphanedBindingTeardownCommands(o));

    // Scene-wide heal, redirecting dangling refs to Base ('' = revert-to-base at
    // runtime). Runs AFTER the demotion so `rewriteRoot` and paths reflect the
    // live hierarchy; folded into the SAME 'Delete state' undo entry.
    commands.push(...await buildStateRefHealCommands(rewriteRoot, name, ''));
    pushBundle('Delete state', commands);
    return true;
  });
  return ok;
}

// --- 16. Ungroup (B1′ keep-portable) ---------------------------------------

/**
 * Authored local pose of `obj` from its serialized SceneObjectData, as a
 * TransformOverride for comparison. The "rest" pose, independent of any active
 * state. Used to recognize a redundant keyed pose on ungroup.
 */
function authoredLocalPose(obj: SceneObject): ResolvedPose {
  const t = obj.getSceneObjectData().transform;
  return {
    position: t?.translation ?? [0, 0, 0],
    rotation: t?.rotation ?? [0, 0, 0, 1],
    scale: t?.scale ?? [1, 1, 1],
  };
}

/**
 * Component-wise near-equality of a STATE transform override `a` against the
 * authored pose `b` (default ε for float noise). A component MISSING on the state
 * side (`a`) is "no override for that component" → it falls back to authored at
 * runtime, so it counts as EQUAL (don't treat a partial `{position}`-only legacy
 * override as differing just because it omits scale/rotation). `b` (authoredLocalPose)
 * always supplies all three components.
 */
function transformsApproxEqual(a: TransformOverride, b: TransformOverride, eps = 1e-5): boolean {
  // `b` is always authoredLocalPose(obj), which supplies all three components, so
  // only the STATE side (`a`) can omit a component (= no override → matches authored).
  const close = (x: readonly TransformComponent[] | undefined, y: readonly TransformComponent[]): boolean => {
    if (x === undefined) return true;        // no override for this component → matches authored
    const xs = numericTuple(x);
    const ys = numericTuple(y);
    // A bound component is never "the authored pose" — its whole point is to
    // differ from it as its driver moves.
    if (!xs || !ys) return false;
    if (xs.length !== ys.length) return false;
    return xs.every((v, i) => Math.abs(v - ys[i]) <= eps);
  };
  return close(a.position, b.position ?? [0, 0, 0])
    && close(a.rotation, b.rotation ?? [0, 0, 0, 1])
    && close(a.scale, b.scale ?? [1, 1, 1]);
}

/**
 * Called after `obj` leaves a stateful group. B1′: KEEP states that carry
 * overrides (portable deltas-over-Base), DROP only empty seed placeholders.
 * Returns the undo command WITHOUT pushing it, so the caller can bundle it with
 * the reparent command into one atomic undo entry. No-op unless the object has
 * states, WAS in a stateful group, and is NOT in one now. Refused in Play, it
 * prunes nothing and returns null, so a host that refuses states edits in Play
 * refuses the reparent as well, or the seeds stay.
 *
 * Also drops a "default-seed" state — one whose only content is the auto-applied
 * default transition and/or a keyed pose that just echoes the authored base.
 * These are the scaffolding every creation path now leaves on seeded members; the
 * user never authored a real override there. A seed whose keyed pose DIFFERS from
 * authored is a real pose and is kept (transition and all).
 *
 * BOUNDARY OWNERS ARE EXEMPT: an object carrying the `stateGroup` marker owns an
 * INDEPENDENT group — its states are authored deltas, not seeds fanned from the
 * outer group it happened to sit inside. Reparenting it out of that group must
 * keep every state verbatim (they are portable-over-Base by construction), so we
 * short-circuit before any pruning. (Its own resolution already reports
 * `nowInGroup === false` via the boundary-aware ancestor walk; this guard is
 * about NOT pruning its owned states, which the seed logic below would.)
 */
export async function dropStatesOnUngroup(
  obj: SceneObject,
  prevParent: SceneObject | null | undefined,
): Promise<UndoCommand | null> {
  const props = ownProperties(obj);
  if (!props || props.getStateNames().length === 0) return null;
  // A boundary owner's states are owned, not seeded — never prune them on ungroup.
  if (isGroupBoundary(obj)) return null;
  // "was in a group" = the PREVIOUS parent chain had a stateful owner.
  // "now in a group" = a STRICT ancestor (above self) still owns states. Self is
  // excluded because a seeded member always carries its own states and would
  // otherwise look like its own group. The previous owner is resolved via the
  // OWNER walk so the prune scope is the group's authoritative name set, not a
  // possibly-drifted member mirror — the same scoping createNestedStateGroup
  // uses, keeping the two decouple paths' prunes identical (the unity point).
  const prevOwner = prevParent ? stateScopeOwner(prevParent) : null;
  const nowInGroup = nearestStrictAncestorOwner(obj) !== null;
  if (!prevOwner || nowInGroup) return null;

  // Only the PREVIOUS group's names can be seeds it fanned in — a state named
  // outside that set is the object's own authoring and travels with it.
  return pruneSeedStates(obj, new Set(prevOwner.getStateNames()));
}

/**
 * Drop an object's REDUNDANT seed states — the scaffolding an outer group's
 * fan-out leaves on a member (a keyed pose/visibility that merely echoes the
 * object's authored base, ± the default transition, with no real value/enterFrom
 * override). Keeps every state that carries a genuine authored override. Returns
 * the prune command (unpushed, for the caller to bundle) or `null` when nothing
 * is redundant.
 *
 * `seedNames` scopes the prune to the names the decoupled-from group actually
 * owns — only those can be fan-in seeds. A state named outside the set is the
 * object's OWN authoring and survives even while empty; without this scoping the
 * separate→rejoin→separate round-trip would eat the user's not-yet-filled states.
 *
 * The SINGLE prune implementation, shared by the two events that decouple an
 * object from its parent group: reparenting OUT ({@link dropStatesOnUngroup}) and
 * SEPARATING via the marker ({@link createNestedStateGroup}). Both want identical
 * "keep real overrides, discard inherited placeholders" semantics — this is the
 * unity point so they can't drift.
 */
async function pruneSeedStates(obj: SceneObject, seedNames: ReadonlySet<string>): Promise<UndoCommand | null> {
  const props = ownProperties(obj);
  if (!props) return null;
  const authored = authoredLocalPose(obj);
  // Authored base visibility for the redundancy comparison. A `$ref`-driven
  // active is real authored content we can't compare against — treat any keyed
  // boolean as meaningful.
  const authoredActiveRaw = obj.getSceneObjectData().active;
  const authoredActive = typeof authoredActiveRaw === 'boolean' ? authoredActiveRaw : true;
  const activeComparable = typeof authoredActiveRaw !== 'string';
  const data = props.getData();
  const kept: Record<string, unknown> = {};
  for (const [name, def] of Object.entries(data.states ?? {})) {
    // Only names the group could have fanned in are seed candidates — a state
    // named outside `seedNames` is the object's own authoring, kept even when
    // (still) empty: the user just hasn't put anything in it yet.
    if (!seedNames.has(name)) { kept[name] = def; continue; }
    if (!statesModel.stateHasOverrides(def)) continue; // truly empty seed → drop
    // Default-seed (only a transition ± a keyed pose ± a keyed visibility).
    // Droppable when BOTH keyed snapshots add nothing — the pose is absent or
    // equals the authored base, and the active is absent or echoes the authored
    // visibility. A pose or visibility that DIFFERS from authored is real → keep.
    if (statesModel.stateIsDefaultSeed(def)) {
      const poseIsRedundant = !def.transform || transformsApproxEqual(def.transform, authored);
      const activeIsRedundant = def.active === undefined || (activeComparable && def.active === authoredActive);
      if (poseIsRedundant && activeIsRedundant) continue;
    }
    kept[name] = def;
  }

  // Nothing changed (all states carried overrides) → no command.
  const before = data.states ?? {};
  if (Object.keys(kept).length === Object.keys(before).length) return null;

  return withGuard(obj, null, async () => {
    return buildPropsCommand(obj, { states: kept } as Partial<PropertiesData>);
  });
}
